import { Component, DestroyRef, EventEmitter, inject, Input, OnInit, Output, Signal, signal } from '@angular/core';
import { ButtonsModule, FormFieldModule, IconModule, MenuModule, SuffixDirective } from '@mastechnicaegroup/carelinelive-ui';
import { CommissionerSingleResponse } from '@lib/api/models/commissioner';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms'
import { castToPersonDisplayData, getDisplayName } from '@lib/api/models/person';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { RefreshableResult } from '@mastechnicaegroup/carelinelive-cdk/rx';
import { CommissionerService } from '@lib/commissioner/commissioner.service';
import { AuthenticationService } from '@lib/auth/authentication.service';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { ApiResponse } from '@lib/api/responses/api-response';
import { map, shareReplay, switchMap } from 'rxjs';
import { FilteredSelectComponent, SelectOption } from '@shared/components/filtered-select/filtered-select.component';
import { AsyncPipe } from '@angular/common';
import { UserOption } from '@lib/api/models/user';
import { CommissionerUser } from '@lib/api/models/commissioner-users';

@Component({
    selector: 'app-commissioner-users-tab',
    imports: [
        AsyncPipe,
        ButtonsModule,
        FilteredSelectComponent,
        FormFieldModule,
        IconModule,
        MenuModule,
        ReactiveFormsModule,
        RouterLink,
        SuffixDirective
    ],
    templateUrl: './commissioner-users-tab.component.html',
    standalone: true,
    styleUrl: './commissioner-users-tab.component.scss'
})
export class CommissionerUsersTabComponent implements OnInit {

    @Input() commissionerData: RefreshableResult<CommissionerSingleResponse> | undefined;
    @Output() removeUserEvent = new EventEmitter<string>();
    @Output() addUserEvent = new EventEmitter<{ user_id: string, role: string}>();
    @Output() editUserPositionEvent: EventEmitter<CommissionerUser> = new EventEmitter<CommissionerUser>();

    private readonly _activatedRoute = inject(ActivatedRoute);
    private readonly _commissionerService = inject(CommissionerService);
    private readonly _authService = inject(AuthenticationService);
    private readonly _destroyRef = inject(DestroyRef);

    protected readonly userControl = new FormGroup({
        user: new FormControl<string>('', [
            Validators.required,
            Validators.minLength(1)
        ]),
        role: new FormControl<string>('', [
            Validators.required
        ])
    });


    protected user = toSignal(this._authService.user$);
    protected readonly getDisplayName = getDisplayName;

    protected readonly selectedUsers = signal<{ id: string }[]>([]);

    protected readonly userResult: Signal<ApiResponse<UserOption[]> | undefined> = toSignal(
        this._activatedRoute.params.pipe(
            map(({ id }) => id),
            switchMap(commissionerId => {
                return this._commissionerService.getAllUsersCanAdd(commissionerId)
            }),

            // Share the same result with all new subscribers
            shareReplay(1),
            takeUntilDestroyed(this._destroyRef)
        )
    );

    protected readonly users$ = toObservable(this.userResult).pipe(
        map((response): { value: UserOption, label: string }[] => {
            return (response?.data??[]).map(user => ({
                value: user,
                label: getDisplayName(castToPersonDisplayData(user.person))
            }));
        }),
        shareReplay(1)
    );

    protected userSearchShown = false;

    ngOnInit() {
        this.commissionerData?.result$.pipe(
            takeUntilDestroyed(this._destroyRef)
        ).subscribe(commissioner => {
            const commissionerSelectedUsers = commissioner.value?.data.users.map(commissionerUser => ({
                id: commissionerUser.user.id
            }));
            if (commissionerSelectedUsers) {
                this.selectedUsers.set(commissionerSelectedUsers);
            }
        });
    }

    addUser() {
        this.addUserEvent.emit({
            user_id: this.userControl.get('user')?.value?? '',
            role: this.userControl.get('role')?.value ?? '',
        });
    }

    removeUser(userId: string) {
        this.removeUserEvent.emit(userId);
    }

    selectedUser($event: SelectOption) {
        this.userControl.get('user')?.setValue($event.value.id);
    }

    editUserPosition(user: CommissionerUser) {
        this.editUserPositionEvent.emit(user);
    }

    toggleUserDropDown() {
        this.userControl.get('user')?.reset();
        this.userControl.get('role')?.reset();
        this.userControl.get('role')?.markAsUntouched();
        this.userSearchShown = !this.userSearchShown;
    }
}
